import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { insertVariedadeSchema, type InsertVariedade, type VariedadeWithCultura, type Cultura } from "@shared/schema";
import { CULTURA_BANANA } from "@shared/culturas";
import { useCulturas, useCreateCultura, useUpdateCultura, useDeleteCultura } from "@/hooks/use-culturas";
import { useSetores } from "@/hooks/use-setores";
import { Plus, Leaf, Pencil, Trash2, RefreshCw } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { PageHeader, ALVO_TOQUE_BOTAO } from "@/components/layout/page-header";
import { PageToolbar, PageSearch } from "@/components/layout/page-toolbar";
import { DataCard } from "@/components/data/data-card";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow,
  DataTableHead, DataTableCell, DataTableNum,
} from "@/components/data/data-table";
import { StatusBadge } from "@/components/data/status-badge";
import { EmptyState, NadaEncontrado } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { combina } from "@/lib/busca";
/** `apiRequest` lança `Error("STATUS: {json}")` — recupera a mensagem real do servidor
 * (ex.: 409 "Esta cultura tem variedade(s) cadastrada(s)...") em vez do corpo cru. */
function mensagemDoErro(e: unknown, fallback: string): string {
  const raw = e instanceof Error ? e.message : String(e);
  const corpo = raw.slice(raw.indexOf(":") + 1).trim();
  try {
    const parsed = JSON.parse(corpo);
    return parsed.error ?? fallback;
  } catch {
    return fallback;
  }
}

const mesesNomes = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

const defaultSemanasMaturacao: Record<string, number> = {};
for (let i = 1; i <= 12; i++) defaultSemanasMaturacao[i.toString()] = 13;

const COLUNAS_VARIEDADES = [null, "7rem", "8rem", "6rem"];
const COLUNAS_VARIEDADES_BANANA = [null, "7rem", "8rem", "22rem", "8rem", "9rem"];

// ⚠️ Constante de MÓDULO (dep de useMemo dentro do hook).
const PADRAO_BUSCA = { q: "" };

export default function VariedadesPage() {
  const { toast } = useToast();
  const { data: culturas, isLoading: culturasLoading } = useCulturas();
  const { data: variedades, isLoading: variedadesLoading } = useQuery<VariedadeWithCultura[]>({
    queryKey: ["/api/variedades"],
  });
  const { setores } = useSetores();

  // Busca NO CLIENTE: recorta VARIEDADES, e a seção da cultura que ficou sem
  // nenhuma some junto — cabeçalho de cultura sobrando sobre lista vazia é pior
  // que não mostrar a seção.
  const { estado, definir } = useEstadoNaUrl(PADRAO_BUSCA);
  const busca = estado.q;
  const variedadesVisiveisDaCultura = (culturaId: string) =>
    (variedades ?? []).filter(v => v.culturaId === culturaId && combina(busca, v.nome));
  const culturasVisiveis = (culturas ?? []).filter(c =>
    // Sem busca, toda cultura aparece — inclusive a que ainda não tem variedade,
    // porque é dela que sai o "Adicionar variedade".
    !busca.trim() || variedadesVisiveisDaCultura(c.id).length > 0,
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVariedade, setEditingVariedade] = useState<VariedadeWithCultura | null>(null);
  const [deletingVariedade, setDeletingVariedade] = useState<VariedadeWithCultura | null>(null);
  const [recalcTarget, setRecalcTarget] = useState<VariedadeWithCultura | null>(null);

  const [culturaDialogOpen, setCulturaDialogOpen] = useState(false);
  const [editingCultura, setEditingCultura] = useState<Cultura | null>(null);
  const [culturaNomeInput, setCulturaNomeInput] = useState("");
  const [culturaFormError, setCulturaFormError] = useState<string | null>(null);
  const [deletingCultura, setDeletingCultura] = useState<Cultura | null>(null);
  const [culturaDeleteError, setCulturaDeleteError] = useState<string | null>(null);

  const createCultura = useCreateCultura();
  const updateCultura = useUpdateCultura();
  const deleteCultura = useDeleteCultura();

  const form = useForm<InsertVariedade>({
    resolver: zodResolver(insertVariedadeSchema),
    defaultValues: {
      nome: "",
      pesoMedioCacho: "15.00",
      semanasMaturacaoPorMes: defaultSemanasMaturacao,
      ativo: true,
      culturaId: "",
    },
  });

  const culturaSelecionada = culturas?.find(c => c.id === form.watch("culturaId"));
  const mostrarCamposBanana = culturaSelecionada ? culturaSelecionada.slug === CULTURA_BANANA : true;

  const createMutation = useMutation({
    mutationFn: async (data: InsertVariedade) => {
      const response = await apiRequest("POST", "/api/variedades", data);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/variedades"] });
      toast({ title: "Variedade cadastrada!", description: "A variedade foi adicionada com sucesso." });
      setDialogOpen(false);
      setEditingVariedade(null);
      form.reset();
    },
    onError: (error: any) => {
      toast({ title: "Erro ao cadastrar variedade", description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: InsertVariedade & { id: string }) => {
      const { id, ...updateData } = data;
      const response = await apiRequest("PATCH", `/api/variedades/${id}`, updateData);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/variedades"] });
      toast({ title: "Variedade atualizada!", description: "As alterações foram salvas com sucesso." });
      setDialogOpen(false);
      setEditingVariedade(null);
      form.reset();
    },
    onError: (error: any) => {
      toast({ title: "Erro ao atualizar variedade", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/variedades/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/variedades"] });
      toast({ title: "Variedade excluída!", description: "A variedade foi removida com sucesso." });
      setDeletingVariedade(null);
    },
    onError: (error: any) => {
      toast({ title: "Erro ao excluir variedade", description: error.message, variant: "destructive" });
    },
  });

  const recalcMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("POST", `/api/variedades/${id}/recalcular-previsoes`, {});
      return await response.json() as { mangarasAtualizados: number; ciclosAfetados: number };
    },
    onSuccess: (result) => {
      // Invalida tudo que depende das previsões dos mangarás (chaves são URLs completas).
      queryClient.invalidateQueries({
        predicate: (q) => {
          const k = q.queryKey?.[0];
          return typeof k === "string" && (
            k.startsWith("/api/mangaras") ||
            k.startsWith("/api/ciclos") ||
            k.startsWith("/api/colheitas") ||
            k.startsWith("/api/dashboard") ||
            k.startsWith("/api/relatorios") ||
            k.startsWith("/api/previsao-semanal")
          );
        },
      });
      toast({
        title: "Previsões recalculadas",
        description: `${result.mangarasAtualizados} ${result.mangarasAtualizados === 1 ? "mangará atualizado" : "mangarás atualizados"} em ${result.ciclosAfetados} ${result.ciclosAfetados === 1 ? "ciclo ativo" : "ciclos ativos"}.`,
      });
      setRecalcTarget(null);
    },
    onError: (error: any) => {
      toast({ title: "Erro ao recalcular previsões", description: error.message, variant: "destructive" });
    },
  });

  // Compara os calendários de maturação (independente da ordem das chaves).
  const semanasMaturacaoIguais = (
    a: Record<string, number> | undefined,
    b: Record<string, number> | undefined,
  ) => {
    const keys = Array.from(new Set(Object.keys(a || {}).concat(Object.keys(b || {}))));
    return keys.every(k => Number(a?.[k]) === Number(b?.[k]));
  };

  const onSubmit = (data: InsertVariedade) => {
    if (editingVariedade) {
      const mudouCalendario = !semanasMaturacaoIguais(
        data.semanasMaturacaoPorMes as Record<string, number>,
        editingVariedade.semanasMaturacaoPorMes as Record<string, number>,
      );
      const target = editingVariedade;
      updateMutation.mutate(
        { ...data, id: editingVariedade.id },
        {
          // Após salvar, se o calendário mudou, oferece o recálculo das fitas ativas.
          onSuccess: () => {
            if (mudouCalendario) setRecalcTarget(target);
          },
        },
      );
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (variedade: VariedadeWithCultura) => {
    setEditingVariedade(variedade);
    form.reset({
      nome: variedade.nome,
      pesoMedioCacho: variedade.pesoMedioCacho,
      semanasMaturacaoPorMes: variedade.semanasMaturacaoPorMes as Record<string, number>,
      ativo: variedade.ativo,
      culturaId: variedade.culturaId,
    });
    setDialogOpen(true);
  };

  const handleNewVariedade = (culturaIdPreset?: string) => {
    setEditingVariedade(null);
    form.reset({
      nome: "",
      pesoMedioCacho: "15.00",
      semanasMaturacaoPorMes: defaultSemanasMaturacao,
      ativo: true,
      culturaId: culturaIdPreset ?? culturas?.[0]?.id ?? "",
    });
    setDialogOpen(true);
  };

  // ─── Culturas ───────────────────────────────────────────────────────────────
  const openNewCultura = () => {
    setEditingCultura(null);
    setCulturaNomeInput("");
    setCulturaFormError(null);
    setCulturaDialogOpen(true);
  };

  const openEditCultura = (cultura: Cultura) => {
    setEditingCultura(cultura);
    setCulturaNomeInput(cultura.nome);
    setCulturaFormError(null);
    setCulturaDialogOpen(true);
  };

  const handleSaveCultura = async () => {
    setCulturaFormError(null);
    try {
      if (editingCultura) {
        await updateCultura.mutateAsync({ id: editingCultura.id, data: { nome: culturaNomeInput.trim() } });
        toast({ title: "Cultura atualizada!" });
      } else {
        await createCultura.mutateAsync({ nome: culturaNomeInput.trim() });
        toast({ title: "Cultura cadastrada!" });
      }
      setCulturaDialogOpen(false);
    } catch (e) {
      setCulturaFormError(mensagemDoErro(e, "Erro ao salvar cultura"));
    }
  };

  const handleDeleteCultura = async () => {
    if (!deletingCultura) return;
    setCulturaDeleteError(null);
    try {
      await deleteCultura.mutateAsync(deletingCultura.id);
      toast({ title: "Cultura excluída!" });
      setDeletingCultura(null);
    } catch (e) {
      setCulturaDeleteError(mensagemDoErro(e, "Erro ao excluir cultura"));
    }
  };

  const isLoading = culturasLoading || variedadesLoading;

  return (
    <div className="space-y-8">
      {/* Os dois botões dividem a linha no celular (44px cada) em vez de um
          deles ser empurrado pra fora da tela. */}
      <PageHeader
        titulo="Culturas e variedades"
        descricao="Lavouras e variedades da fazenda"
        acoesSecundarias={
          <Button variant="outline" onClick={openNewCultura} className={`flex-1 md:flex-none ${ALVO_TOQUE_BOTAO}`} data-testid="button-new-cultura">
            <Plus className="h-4 w-4 mr-2" />
            Nova cultura
          </Button>
        }
        acao={
          <Button onClick={() => handleNewVariedade()} className={`flex-1 md:flex-none ${ALVO_TOQUE_BOTAO}`} data-testid="button-new-variedade">
            <Plus className="h-4 w-4 mr-2" />
            Nova variedade
          </Button>
        }
      />

      <PageToolbar
        fim={<PageSearch valor={busca} onChange={v => definir({ q: v })} placeholder="Variedade" testId="input-busca-variedades" />}
      >
        <span />
      </PageToolbar>

      {/* Seções por cultura — a HIERARQUIA fica. Cada cultura é uma seção com
          cabeçalho próprio; achatar tudo numa tabela só apagaria a informação de
          que variedade pertence a cultura, que é o eixo desta tela. */}
      {isLoading ? (
        <PageSkeleton variante="tabela" linhas={6} />
      ) : culturasVisiveis.length === 0 ? (
        busca.trim()
          ? <NadaEncontrado termo={busca} />
          : <EmptyState
              icone={Leaf}
              titulo="Nenhuma cultura cadastrada"
              descricao="A cultura agrupa as variedades — crie uma para começar a cadastrar."
              testId="empty-variedades"
            />
      ) : (
        <div className="space-y-6">
          {culturasVisiveis.map((cultura) => {
            const variedadesDaCultura = variedadesVisiveisDaCultura(cultura.id);
            const variedadeIds = new Set(variedadesDaCultura.map(v => v.id));
            const setoresDaCultura = (setores ?? []).filter(s => s.variedadeId && variedadeIds.has(s.variedadeId));
            const ehGlobal = cultura.orgId === null;
            const ehBanana = cultura.slug === CULTURA_BANANA;

            return (
              <section key={cultura.id} data-testid={`cultura-section-${cultura.id}`}>
                <DataCard
                  titulo={cultura.nome}
                  meta={
                    <>
                      {/* Cultura padrão é CATEGORIA: outline neutro. */}
                      {ehGlobal && <Badge variant="outline" className="text-xs font-normal mr-2">Padrão</Badge>}
                      {variedadesDaCultura.length} {variedadesDaCultura.length === 1 ? "variedade" : "variedades"}
                      {" · "}
                      {setoresDaCultura.length} {setoresDaCultura.length === 1 ? "setor" : "setores"}
                    </>
                  }
                  acao={
                    !ehGlobal ? (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-muted-foreground"
                        onClick={() => openEditCultura(cultura)}
                        data-testid={`button-edit-cultura-${cultura.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    ) : undefined
                  }
                >
                  {variedadesDaCultura.length === 0 ? (
                    <EmptyState
                      compacto
                      icone={Leaf}
                      titulo="Nenhuma variedade nesta cultura"
                      descricao="A variedade é quem carrega o peso médio do cacho e as semanas de maturação."
                      acao={
                        <Button size="sm" variant="outline" onClick={() => handleNewVariedade(cultura.id)}>
                          <Plus className="h-4 w-4 mr-2" />
                          Adicionar variedade
                        </Button>
                      }
                    />
                  ) : (
                    <DataTable colunas={ehBanana ? COLUNAS_VARIEDADES_BANANA : COLUNAS_VARIEDADES}>
                      <DataTableHeader>
                        <DataTableRow>
                          <DataTableHead>Variedade</DataTableHead>
                          <DataTableHead num>Setores</DataTableHead>
                          {ehBanana && <DataTableHead num>Peso médio</DataTableHead>}
                          {ehBanana && <DataTableHead>Maturação por mês</DataTableHead>}
                          <DataTableHead>Situação</DataTableHead>
                          <DataTableHead />
                        </DataTableRow>
                      </DataTableHeader>
                      <DataTableBody>
                        {variedadesDaCultura.map((variedade) => {
                          const nSetores = (setores ?? []).filter(s => s.variedadeId === variedade.id).length;
                          return (
                            <DataTableRow
                              key={variedade.id}
                              className="cursor-pointer"
                              onClick={() => handleEdit(variedade)}
                              data-testid={`variedade-card-${variedade.id}`}
                            >
                              {/* A variedade é a coluna que responde à pergunta da tela. */}
                              <DataTableCell destaque>
                                <span className="flex items-center gap-2 min-w-0">
                                  <Leaf className="h-4 w-4 text-primary shrink-0" />
                                  <span className="truncate">{variedade.nome}</span>
                                </span>
                              </DataTableCell>
                              <DataTableNum>{nSetores}</DataTableNum>
                              {ehBanana && <DataTableNum>{variedade.pesoMedioCacho} kg</DataTableNum>}
                              {ehBanana && (
                                <DataTableCell className="text-xs text-muted-foreground truncate">
                                  {/* A grade 4×3 de meses não cabe numa linha de tabela:
                                      vira resumo, e o detalhe fica na edição. */}
                                  {Object.entries(variedade.semanasMaturacaoPorMes as Record<string, number>)
                                    .map(([mes, semanas]) => `${mesesNomes[parseInt(mes) - 1].slice(0, 3)} ${semanas}s`)
                                    .join(" · ")}
                                </DataTableCell>
                              )}
                              <DataTableCell>
                                {variedade.ativo
                                  ? <StatusBadge tom="sucesso" ponto>Ativo</StatusBadge>
                                  : <StatusBadge tom="neutro">Inativo</StatusBadge>}
                              </DataTableCell>
                              <DataTableCell className="text-right" onClick={e => e.stopPropagation()}>
                                <div className="flex gap-1 justify-end">
                                  {ehBanana && (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => setRecalcTarget(variedade)}
                                      title="Recalcular previsões das fitas ativas"
                                      data-testid={`button-recalcular-${variedade.id}`}
                                    >
                                      <RefreshCw className="h-4 w-4" />
                                    </Button>
                                  )}
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => handleEdit(variedade)}
                                    data-testid={`button-edit-${variedade.id}`}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="text-destructive hover:text-destructive"
                                    onClick={() => setDeletingVariedade(variedade)}
                                    data-testid={`button-delete-${variedade.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </DataTableCell>
                            </DataTableRow>
                          );
                        })}
                      </DataTableBody>
                    </DataTable>
                  )}
                </DataCard>
              </section>
            );
          })}
        </div>
      )}

      {/* Delete Variedade Alert Dialog */}
      <AlertDialog open={!!deletingVariedade} onOpenChange={(open) => { if (!open) setDeletingVariedade(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir variedade?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a variedade <strong>{deletingVariedade?.nome}</strong>? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (deletingVariedade) deleteMutation.mutate(deletingVariedade.id); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Recalcular Previsões Alert Dialog */}
      <AlertDialog open={!!recalcTarget} onOpenChange={(open) => { if (!open) setRecalcTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Recalcular previsões das fitas ativas?</AlertDialogTitle>
            <AlertDialogDescription>
              Os mangarás de ciclos em andamento da variedade <strong>{recalcTarget?.nome}</strong> passam a usar as novas semanas de maturação. Ciclos fechados não mudam.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-recalcular-cancelar">Agora não</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (recalcTarget) recalcMutation.mutate(recalcTarget.id); }}
              disabled={recalcMutation.isPending}
              data-testid="button-recalcular-confirmar"
            >
              {recalcMutation.isPending ? "Recalculando..." : "Recalcular"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Excluir Cultura Alert Dialog */}
      <AlertDialog open={!!deletingCultura} onOpenChange={(open) => { if (!open) { setDeletingCultura(null); setCulturaDeleteError(null); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir cultura?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a cultura <strong>{deletingCultura?.nome}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          {culturaDeleteError && (
            <p className="text-sm text-destructive" data-testid="text-cultura-delete-error">{culturaDeleteError}</p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCultura}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteCultura.isPending}
              data-testid="button-confirm-delete-cultura"
            >
              {deleteCultura.isPending ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create/Edit Cultura Dialog */}
      <Dialog open={culturaDialogOpen} onOpenChange={setCulturaDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCultura ? "Editar Cultura" : "Nova Cultura"}</DialogTitle>
            <DialogDescription>
              {editingCultura ? "Renomeie a cultura." : "Cadastre uma lavoura fora do catálogo padrão (banana, citros)."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cultura-nome">Nome *</Label>
            <Input
              id="cultura-nome"
              placeholder="Ex: Café, Manga..."
              value={culturaNomeInput}
              onChange={(e) => setCulturaNomeInput(e.target.value)}
              data-testid="input-cultura-nome"
            />
            {culturaFormError && (
              <p className="text-xs text-destructive" data-testid="text-cultura-form-error">{culturaFormError}</p>
            )}
          </div>
          <DialogFooter className="flex-col-reverse md:flex-row gap-2">
            {editingCultura && (
              <Button
                type="button"
                variant="ghost"
                className="text-destructive hover:text-destructive md:mr-auto"
                onClick={() => { setCulturaDialogOpen(false); setDeletingCultura(editingCultura); }}
                data-testid="button-delete-cultura"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => setCulturaDialogOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleSaveCultura}
              disabled={!culturaNomeInput.trim() || createCultura.isPending || updateCultura.isPending}
              data-testid="button-submit-cultura"
            >
              {createCultura.isPending || updateCultura.isPending ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Variedade Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) { setEditingVariedade(null); form.reset(); }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">
              {editingVariedade ? "Editar Variedade" : "Nova Variedade"}
            </DialogTitle>
            <DialogDescription>
              {editingVariedade
                ? "Atualize os parâmetros da variedade."
                : "Cadastre uma nova variedade e a cultura a que ela pertence."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome da Variedade *</Label>
                <Input
                  id="nome"
                  placeholder="Ex: Prata, Nanica, Pêra Rio..."
                  data-testid="input-nome"
                  {...form.register("nome")}
                />
                {form.formState.errors.nome && (
                  <p className="text-xs text-destructive">{form.formState.errors.nome.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="culturaId">Cultura *</Label>
                <Select
                  value={form.watch("culturaId") || undefined}
                  onValueChange={v => form.setValue("culturaId", v, { shouldValidate: true })}
                >
                  <SelectTrigger id="culturaId" data-testid="select-cultura">
                    <SelectValue placeholder="Selecione a cultura" />
                  </SelectTrigger>
                  <SelectContent>
                    {culturas?.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.formState.errors.culturaId && (
                  <p className="text-xs text-destructive">{form.formState.errors.culturaId.message}</p>
                )}
              </div>

              {mostrarCamposBanana && (
                <div className="space-y-2">
                  <Label htmlFor="pesoMedioCacho">Peso Médio do Cacho (kg) *</Label>
                  <Input
                    id="pesoMedioCacho"
                    type="number"
                    step="0.01"
                    placeholder="15.00"
                    data-testid="input-peso"
                    {...form.register("pesoMedioCacho")}
                  />
                  {form.formState.errors.pesoMedioCacho && (
                    <p className="text-xs text-destructive">{form.formState.errors.pesoMedioCacho.message}</p>
                  )}
                </div>
              )}
            </div>

            {mostrarCamposBanana && (
              <div className="space-y-4">
                <Label>Semanas de Maturação por Mês *</Label>
                <p className="text-xs text-muted-foreground">
                  Configure quantas semanas após o corte do mangará a colheita é prevista para cada mês.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {mesesNomes.map((mesNome, index) => {
                    const mesNum = (index + 1).toString();
                    return (
                      <div key={mesNum} className="space-y-2">
                        <Label htmlFor={`mes-${mesNum}`} className="text-xs">{mesNome}</Label>
                        <Input
                          id={`mes-${mesNum}`}
                          type="number"
                          min="4"
                          max="26"
                          className="font-mono"
                          {...form.register(`semanasMaturacaoPorMes.${mesNum}` as any, { valueAsNumber: true })}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <DialogFooter className="flex-col-reverse md:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="button-cancel">
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                data-testid="button-submit"
              >
                {(createMutation.isPending || updateMutation.isPending)
                  ? "Salvando..."
                  : editingVariedade
                    ? "Salvar Alterações"
                    : "Cadastrar Variedade"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
